import { defineStore } from 'pinia';
import type { AddWalletBodyType, UpdateWalletBodyType, UserInfosType } from '~/types';

export const useAddWalletStore = defineStore('add-wallet', {
  state: () => ({
    wallet: { value: undefined as string | undefined, isValid: undefined as boolean | undefined },
    walletIntId: {
      value: undefined as string | undefined,
      isValid: undefined as boolean | undefined
    },
    payerOrBeneficiary: ['1'] as Array<string>,
    isOneTimeCustomer: undefined as boolean | undefined,
    isTechWallet: undefined as boolean | undefined,
    isDebtor: undefined as boolean | undefined,
    clientFirstName: {
      value: undefined as string | undefined,
      isValid: undefined as boolean | undefined
    },
    clientLastName: {
      value: undefined as string | undefined,
      isValid: undefined as boolean | undefined
    },
    birthdate: {
      value: undefined as string | undefined,
      isValid: undefined as boolean | undefined
    },
    birthcity: {
      value: undefined as string | undefined,
      isValid: undefined as boolean | undefined
    },
    clientMail: {
      value: undefined as string | undefined,
      isValid: undefined as boolean | undefined
    },
    phoneNumber: {
      value: undefined as string | undefined,
      isValid: undefined as boolean | undefined
    },
    mobileNumber: {
      value: undefined as string | undefined,
      isValid: undefined as boolean | undefined
    },
    birthcountry: undefined as string | undefined,
    ctry: undefined as string | undefined,
    nationality: [] as Array<string>,
    street: { value: undefined as string | undefined, isValid: undefined as boolean | undefined },
    postCode: { value: undefined as string | undefined, isValid: undefined as boolean | undefined },
    city: { value: undefined as string | undefined, isValid: undefined as boolean | undefined },
    isCompany: false as boolean,
    companyName: {
      value: undefined as string | undefined,
      isValid: undefined as boolean | undefined
    },
    companyIdentificationNumber: {
      value: undefined as string | undefined,
      isValid: undefined as boolean | undefined
    },
    companyDescription: {
      value: undefined as string | undefined,
      isValid: undefined as boolean | undefined
    },
    companyWebsite: {
      value: undefined as string | undefined,
      isValid: undefined as boolean | undefined
    },
    password: {
      value: undefined as string | undefined,
      isValid: undefined as boolean | undefined
    },
    isUltimateBeneficialOwner: ['false'] as Array<string | undefined>,
    language: undefined as string | undefined,
    clientTitle: undefined as string | undefined,
    currentStep: 0 as number,
    sections: ['Wallet information', 'Personal information', 'Contact & Address'] as Array<string>,
    openAddModal: false as boolean,
    editWalletInfos: false as boolean,
    editPersonnalInfos: false as boolean
  }),
  getters: {
    getAddWalletBody(): AddWalletBodyType {
      return {
        wallet: this.wallet.value,
        payerOrBeneficiary: parseInt(this.payerOrBeneficiary[0].toString(), 10),
        isOneTimeCustomer: this.isOneTimeCustomer,
        isTechWallet: this.isTechWallet,
        isDebtor: this.isDebtor,
        clientFirstName: this.clientFirstName.value,
        clientLastName: this.clientLastName.value,
        birthdate: this.birthdate.value,
        birthcity: this.birthcity.value,
        clientMail: this.clientMail.value,
        phoneNumber: this.phoneNumber.value,
        mobileNumber: this.mobileNumber.value,
        birthcountry: this.birthcountry,
        ctry: this.ctry,
        nationality: this.nationality.join(','),
        street: this.street.value,
        postCode: this.postCode.value,
        city: this.city.value,
        isCompany: this.isCompany,
        companyName: this.companyName.value,
        companyIdentificationNumber: this.companyIdentificationNumber.value,
        companyDescription: this.companyDescription.value,
        companyWebsite: this.companyWebsite.value,
        isUltimateBeneficialOwner: this.isUltimateBeneficialOwner[0] === 'true',
        language: this.language,
        clientTitle: 'U',
        Password: this.password.value
      };
    },
    getUpdateWalletBody(): UpdateWalletBodyType {
      return {
        newPayerOrBeneficiary: parseInt(this.payerOrBeneficiary[0], 10),
        newIsDebtor: this.isDebtor,
        newFirstName: this.clientFirstName.value,
        newLastName: this.clientLastName.value,
        newBirthdate: this.birthdate.value,
        newBirthcity: this.birthcity.value,
        newEmail: this.clientMail.value,
        newPhoneNumber: this.phoneNumber.value,
        newMobileNumber: this.mobileNumber.value,
        newBirthcountry: this.birthcountry,
        newCtry: this.ctry,
        newNationality: this.nationality.join(','),
        newStreet: this.street.value,
        newPostCode: this.postCode.value,
        newCity: this.city.value,
        newIsCompany: this.isCompany,
        newCompanyName: this.companyName.value,
        newCompanyIdentificationNumber: this.companyIdentificationNumber.value,
        newCompanyDescription: this.companyDescription.value,
        newCompanyWebsite: this.companyWebsite.value,
        isUltimateBeneficialOwner: this.isUltimateBeneficialOwner[0] === 'true',
        language: this.language,
        newTitle: this.clientTitle,
        Password: this.password.value
      };
    }
  },
  actions: {
    init(initialState: UserInfosType, locale: string) {
      this.walletIntId = { value: initialState.intId?.toString(), isValid: true };
      this.wallet = { value: initialState.wallet, isValid: true };
      this.payerOrBeneficiary = [initialState.payerOrBeneficiary.toString()];
      this.isCompany = initialState.isCompany;
      this.companyName = { value: initialState.companyName, isValid: !!initialState.companyName };
      this.companyIdentificationNumber = {
        value: initialState.companyIdentificationNumber,
        isValid: !!initialState.companyIdentificationNumber
      };
      this.companyDescription = {
        value: initialState.companyDescription,
        isValid: !!initialState.companyDescription
      };
      this.companyWebsite = {
        value: initialState.companyWebsite,
        isValid: !!initialState.companyWebsite
      };
      this.clientFirstName = {
        value: initialState.clientFirstName,
        isValid: !!initialState.clientFirstName
      };
      this.clientLastName = {
        value: initialState.clientLastName,
        isValid: !!initialState.clientLastName
      };
      this.clientMail = { value: initialState.clientMail, isValid: !!initialState.clientMail };
      this.phoneNumber = { value: initialState.phoneNumber, isValid: !!initialState.phoneNumber };
      this.mobileNumber = {
        value: initialState.mobileNumber,
        isValid: !!initialState.mobileNumber
      };
      this.birthdate = {
        value: getFormattedDate(initialState.birthdate, 'L', locale),
        isValid: !!initialState.birthdate
      };
      this.birthcity = { value: initialState.birthcity, isValid: !!initialState.birthcity };
      this.street = { value: initialState.street, isValid: !!initialState.street };
      this.postCode = { value: initialState.postCode, isValid: !!initialState.postCode };
      this.city = { value: initialState.city, isValid: !!initialState.city };
      this.ctry = initialState.ctry;
      this.birthcountry = initialState.birthcountry;
      this.nationality = initialState.nationality ? initialState.nationality.split(',') : [];
      this.isUltimateBeneficialOwner = [initialState?.personId?.toString()];
      this.isDebtor = initialState.isDebtor;
      this.clientTitle = initialState.clientTitle;
      this.password = { value: undefined, isValid: false };
    },
    handleDisableSubmit(steps: Array<{ title: string; value: string }>) {
      switch (steps[this.currentStep].value) {
        case 'wallet':
          return !this.wallet.isValid;
        case 'personal':
          return (
            !this.clientFirstName.isValid ||
            !this.clientLastName.isValid ||
            !this.birthdate.isValid ||
            !this.birthcity.isValid ||
            !this.birthcountry ||
            this.nationality.length < 1
          );
        case 'contact':
          return (
            !this.clientMail.isValid ||
            !this.ctry ||
            !this.street.isValid ||
            !this.postCode.isValid ||
            !this.city.isValid ||
            !this.password.isValid
          );
        case 'company':
          return (
            !this.companyName.isValid ||
            !this.companyIdentificationNumber.isValid ||
            !this.companyDescription.isValid
          );
        case 'beneficial':
          return !this.isUltimateBeneficialOwner;
        default:
          break;
      }
    },
    toggleAddWalletModal() {
      this.openAddModal = !this.openAddModal;
      if (!this.openAddModal) {
        this.resetInputs();
      }
    },
    resetInputs() {
      this.wallet = { value: undefined, isValid: undefined };
      this.payerOrBeneficiary = ['1'];
      this.isOneTimeCustomer = undefined;
      this.isTechWallet = undefined;
      this.isDebtor = undefined;
      this.clientFirstName = {
        value: undefined,
        isValid: undefined
      };
      this.clientLastName = {
        value: undefined,
        isValid: undefined
      };
      this.birthdate = {
        value: undefined,
        isValid: undefined
      };
      this.birthcity = {
        value: undefined,
        isValid: undefined
      };
      this.clientMail = {
        value: undefined,
        isValid: undefined
      };
      this.phoneNumber = {
        value: undefined,
        isValid: undefined
      };
      this.mobileNumber = {
        value: undefined,
        isValid: undefined
      };
      this.birthcountry = undefined;
      this.ctry = undefined;
      this.nationality = [];
      this.street = { value: undefined, isValid: undefined };
      this.postCode = { value: undefined, isValid: undefined };
      this.city = { value: undefined, isValid: undefined };
      this.isCompany = false;
      this.companyName = {
        value: undefined,
        isValid: undefined
      };
      this.companyIdentificationNumber = {
        value: undefined,
        isValid: undefined
      };
      this.companyDescription = {
        value: undefined,
        isValid: undefined
      };
      this.companyWebsite = {
        value: undefined,
        isValid: undefined
      };
      this.password = {
        value: undefined,
        isValid: undefined
      };
      this.isUltimateBeneficialOwner = ['false'];
      this.language = undefined;
      this.currentStep = 0;
    }
  }
});
